import React from 'react';
import { RefreshCw, PauseCircle } from 'lucide-react';

/**
 * "This brand is getting updated right now."
 *
 * Shown on a card whose brand has a pipeline running against its DOMAIN —
 * usually started by someone else, because an update rebuilds the shared
 * affiliate list rather than one account's copy of it. Deliberately a different
 * colour from the blue "Processing" pill (that one is this card's own first
 * build) and the amber live-check bulb, so three concurrent states stay legible.
 *
 * Says the brand is being updated and nothing more. No stage names, no account
 * of what an update does — that is how the product works, and this is on a
 * customer's screen. The admin's Update Brand page is where the detail lives.
 *
 * Feed it the object from `useBrandUpdate`; it renders nothing when that is null.
 */
const BrandUpdateBadge = ({ update, compact = false }) => {
  if (!update) return null;

  const paused = update.status === 'paused';
  const percent =
    typeof update.percent === 'number' && Number.isFinite(update.percent)
      ? Math.max(0, Math.min(100, Math.round(update.percent)))
      : null;

  // No stage wording anywhere on a card. The pipeline's step names describe how
  // the product works and this is shown to customers, so all they get is that
  // the brand is being updated and how far along it is.
  const title = paused ? 'Update paused' : 'Brand getting updated';

  const tone = paused
    ? 'bg-gray-50 dark:bg-slate-800/60 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-slate-300'
    : 'bg-violet-50 dark:bg-violet-900/30 border-violet-200 dark:border-violet-700 text-violet-700 dark:text-violet-300';

  return (
    <div
      className={`flex items-center gap-1.5 px-2 py-1 border rounded-lg ${tone}`}
      title={title}
    >
      {paused ? (
        <PauseCircle className="w-3 h-3 shrink-0" />
      ) : (
        <RefreshCw className="w-3 h-3 shrink-0 animate-spin" />
      )}
      <span className="text-xs font-medium whitespace-nowrap">
        {paused ? 'Update paused' : compact ? 'Updating' : 'Brand getting updated'}
        {percent === null ? '' : ` · ${percent}%`}
      </span>
    </div>
  );
};

/**
 * The wider line under the header: how far along the update is, with a bar.
 * Only worth the space when the card is not already showing its own progress.
 *
 * Says nothing about what the pipeline is doing — no stage name, no explanation
 * of what an update touches. Just that the brand is being updated.
 */
export const BrandUpdateProgress = ({ update }) => {
  if (!update || update.status === 'paused') return null;

  const percent =
    typeof update.percent === 'number' && Number.isFinite(update.percent)
      ? Math.max(0, Math.min(100, Math.round(update.percent)))
      : null;

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-violet-600 dark:text-violet-400">
          Brand getting updated...
        </span>
        {percent !== null && (
          <span className="text-xs text-violet-500 dark:text-violet-300">{percent}%</span>
        )}
      </div>
      <div className="w-full bg-violet-100 dark:bg-violet-900/40 rounded-full h-1.5 overflow-hidden">
        <div
          className="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${percent === null ? 15 : percent}%` }}
        />
      </div>
    </div>
  );
};

export default BrandUpdateBadge;
